import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Fab,
  TextField,
  InputAdornment,
  Grid,
  Badge,
  Divider,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab
} from '@mui/material';
import { 
  PencilIcon, 
  XMarkIcon, 
  PlusIcon, 
  ViewColumnsIcon, 
  Squares2X2Icon,
  MagnifyingGlassIcon,
  FunnelIcon,
  AdjustmentsHorizontalIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { Product } from '../../order';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { enhancedApiClient, isSuccessResponse } from '../../../../services/enhancedApiClient';

interface ProductTableProps {
  products: Product[];
  onEditProduct: (product: Product) => void;
  onRemoveProduct: (productId: string) => void;
  onAddProduct?: (productType?: string) => void;
  canEdit?: boolean;
  canRemove?: (product: Product) => boolean;
}

const ProductTable: React.FC<ProductTableProps> = ({
  products,
  onEditProduct,
  onRemoveProduct,
  onAddProduct,
  canEdit = true,
  canRemove = () => true
}) => {
  // Debug log to track product updates
  console.log('🔄 ProductTable received products:', products.length, products);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('cards');
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Subtype label map for displaying subtypes
  const [subtypeLabelMap, setSubtypeLabelMap] = useState<Map<string, string>>(new Map());
  const fetchedSubtypesRef = useRef<string>('');

  // Helper function to get label for a subtype value
  const getSubtypeLabel = (
    subtypeName: string,
    value: string,
    productType: string,
    itemType: string
  ): string => {
    const key = `${productType}-${itemType}-${subtypeName}-${value}`;
    return subtypeLabelMap.get(key) || value;
  };

  // Helper function to format subtypes from all items in a product
  const formatProductSubtypes = (product: any): string => {
    const allItems = [
      ...(product.frame || []),
      ...(product.panel || []),
      ...(product.panelWindow || []),
      ...(product.net || []),
      ...(product.openableType || []),
    ];

    const subtypesSet = new Set<string>();
    const productType = product.type || '';
    
    allItems.forEach((item: any) => {
      const subtype = item.subtype;
      const itemType = item.type || '';
      
      if (subtype && typeof subtype === 'object' && !Array.isArray(subtype)) {
        // Format: { "Quality": ["Standard"], "Finish": ["Matte", "Glossy"] }
        Object.keys(subtype).forEach((key) => {
          const values = subtype[key];
          if (Array.isArray(values) && values.length > 0) {
            const labels = values.map((val: string) => 
              getSubtypeLabel(key, val, productType, itemType)
            );
            // Add each label separately (without subtype name)
            labels.forEach(label => subtypesSet.add(label));
          } else if (values) {
            const label = getSubtypeLabel(key, values, productType, itemType);
            subtypesSet.add(label);
          }
        });
      }
    });

    if (subtypesSet.size === 0) {
      return '-';
    }

    // Return first few subtypes, or all if there are few
    const subtypesArray = Array.from(subtypesSet);
    if (subtypesArray.length <= 2) {
      return subtypesArray.join('; ');
    }
    return `${subtypesArray.slice(0, 2).join('; ')}...`;
  };

  // Fetch subtype definitions to build label map
  useEffect(() => {
    const fetchSubtypeLabels = async () => {
      if (!products || products.length === 0) {
        fetchedSubtypesRef.current = '';
        return;
      }

      // Create a unique key for this product list's product/item type combinations
      const productTypes = new Set<string>();
      const itemTypes = new Set<string>();

      // Collect all product types and item types from all products
      products.forEach((product: any) => {
        if (product.type) productTypes.add(product.type);
        const allItems = [
          ...(product.frame || []),
          ...(product.panel || []),
          ...(product.panelWindow || []),
          ...(product.net || []),
          ...(product.openableType || []),
        ];
        allItems.forEach((item: any) => {
          if (item.type) itemTypes.add(item.type);
        });
      });

      // Create a unique key based on all combinations
      const productTypesKey = Array.from(productTypes).sort().join(',');
      const itemTypesKeyStr = Array.from(itemTypes).sort().join(',');
      const fetchKey = `${productTypesKey}-${itemTypesKeyStr}`;

      // Skip if we've already fetched for this combination
      if (fetchedSubtypesRef.current === fetchKey) {
        return;
      }

      // Mark as fetched
      fetchedSubtypesRef.current = fetchKey;

      const labelMap = new Map<string, string>();

      // Fetch subtype definitions for all combinations
      const fetchPromises: Promise<void>[] = [];
      productTypes.forEach((productType) => {
        itemTypes.forEach((itemType) => {
          const promise = enhancedApiClient
            .get(`/item-subtype-definitions/available?productType=${encodeURIComponent(productType)}&itemType=${encodeURIComponent(itemType)}`)
            .then((response) => {
              if (isSuccessResponse(response) && response.data) {
                const backendResponse = response.data as any;
                const config = backendResponse?.data || backendResponse;
                const subtypes = config?.subtypes || [];

                subtypes.forEach((subtypeGroup: any) => {
                  const subtypeName = subtypeGroup.name || subtypeGroup.subtypeName;
                  if (subtypeGroup.options && Array.isArray(subtypeGroup.options)) {
                    subtypeGroup.options.forEach((option: any) => {
                      const value = option.value || option.optionValue;
                      const label = option.label || option.optionLabel || value;
                      if (value) {
                        const key = `${productType}-${itemType}-${subtypeName}-${value}`;
                        labelMap.set(key, label);
                      }
                    });
                  }
                });
              }
            })
            .catch((error) => {
              console.error(`Error fetching subtypes for ${productType}-${itemType}:`, error);
            });
          fetchPromises.push(promise);
        });
      });

      await Promise.all(fetchPromises);
      setSubtypeLabelMap(labelMap);
    };

    fetchSubtypeLabels();
  }, [products]);

  // Map tabs to product types for Add Product cards
  const getProductTypeForTab = (tabIndex: number): string => {
    switch (tabIndex) {
      case 0: return 'All'; // All products
      case 1: return 'Frame'; // Pending products - default to Frame
      case 2: return 'Panel'; // In Progress products - default to Panel  
      case 3: return 'Door'; // Completed products - default to Door
      default: return 'Frame';
    }
  };

  function getProductTypeCounts(products: Product[]) {
    return products.reduce((acc, product) => {
      const type = product.type || 'unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }

  const typeCounts = useMemo(() => getProductTypeCounts(products), [products]);

  const tabTypes = ['All', 'door', 'window', 'net', 'ventilator'];

  // Filter and sort products based on active tab
  // const filteredProducts = useMemo(() => {
  //   let filtered = products;
    
  //   // Apply tab-based filtering
  //   switch (activeTab) {
  //     case 0: // All products
  //       filtered = products;
  //       break;
  //     case 1: // Pending products
  //       filtered = products.filter(product => product.completed_items === 0);
  //       break;
  //     case 2: // In Progress products
  //       filtered = products.filter(product => product.completed_items > 0 && product.completed_items < product.total_items);
  //       break;
  //     case 3: // Completed products
  //       filtered = products.filter(product => product.completed_items === product.total_items);
  //       break;
  //     default:
  //       filtered = products;
  //   }
    
  //   // Sort by creation date (latest first) - using id as fallback for sorting
  //   return [...filtered].sort((a, b) => {
  //     // Try to use creation date if available, otherwise use id for consistent sorting
  //     const dateA = new Date((a as any).createdAt || (a as any).created_at || a.id);
  //     const dateB = new Date((b as any).createdAt || (b as any).created_at || b.id);
  //     return dateB.getTime() - dateA.getTime();
  //   });
  // }, [products, activeTab]);

  const filteredProducts = useMemo(() => {
    const type = tabTypes[activeTab];
    if (type === 'All') return products;
    return products.filter(product => product.type?.toLowerCase() === type);
  }, [products, activeTab]);

  // Memoize count values to prevent unnecessary re-renders
  const productCounts = useMemo(() => ({
    total: products.length,
    filtered: filteredProducts.length,
    pending: products.filter(p => p.completed_items === 0).length,
    inProgress: products.filter(p => p.completed_items > 0 && p.completed_items < p.total_items).length,
    completed: products.filter(p => p.completed_items === p.total_items).length
  }), [products, filteredProducts.length]);

  // Get unique product types for filter
  const productTypes = useMemo(() => {
    const types = [...new Set(products.map(p => p.type))];
    console.log('Unique product types:', types);
    return types;
  }, [products]);

  // Get product status
  const getProductStatus = (product: Product) => {
    if (product.completed_items === product.total_items) return 'completed';
    if (product.completed_items > 0) return 'In Progress';
    return 'pending';
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress':
      case 'In Progress': return 'warning';
      default: return 'default';
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon className="h-4 w-4" />;
      case 'in_progress':
      case 'In Progress': return <ClockIcon className="h-4 w-4" />;
      default: return <ExclamationTriangleIcon className="h-4 w-4" />;
    }
  };

  // Add Product Card Component
  const AddProductCard = () => {
    const productType = getProductTypeForTab(activeTab);
    
    return (
      <Box>
        <Card 
          variant="outlined" 
          sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            border: '2px dashed',
            borderColor: 'primary.main',
            backgroundColor: 'primary.50',
            transition: 'all 0.2s ease-in-out',
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'primary.dark',
              backgroundColor: 'primary.100',
              transform: 'translateY(-2px)',
              boxShadow: 4
            }
          }}
          onClick={() => onAddProduct?.(productType)}
        >
          <CardContent sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: 3
          }}>
            <Box sx={{ 
              width: 48, 
              height: 48, 
              borderRadius: '50%', 
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2
            }}>
              <PlusIcon className="h-6 w-6" style={{ color: 'white' }} />
            </Box>
            
            <Typography variant="h6" sx={{ 
              fontWeight: 600,
              mb: 1,
              color: 'primary.main'
            }}>
              Add Set
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {productType === 'All' 
                ? 'Add a new set to this order'
                : `Add new ${productType} set`
              }
            </Typography>
            
            <Chip 
              label={productType} 
              size="small" 
              color="primary" 
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          </CardContent>
        </Card>
      </Box>
    );
  };

  // Enhanced Card View Component
  const CardView = () => (
    <Box>
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
        gap: 2, 
        p: 1
      }}>
        {/* Always show Add Product card first */}
        {/* {onAddProduct && <AddProductCard />} */}
        
        {/* Show filtered products */}
        {filteredProducts.length === 0 ? (
          <Box sx={{ 
            gridColumn: '1 / -1',
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            textAlign: 'center',
            py: 6,
            px: 4
          }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No set found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {onAddProduct 
                ? 'Click "Add Set" to add sets to this order'
                : 'This order has no sets'
              }
            </Typography>
          </Box>
        ) : (
          filteredProducts.map((product, productIndex) => {
          const status = getProductStatus(product);
          const progress = product.total_items > 0 ? (product.completed_items / product.total_items) * 100 : 0;
          const isFirstProduct = productIndex === 0;
          
          return (
            <Box key={product.id} >
              <Card 
                variant="outlined" 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => onEditProduct(product)}
              >
                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2, mb:-2}}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" sx={{ 
                        fontSize: '1rem', 
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        mb: 0.5
                      }}>
                        {product.label || product.name || `Set ${productIndex + 1}`}
                      </Typography>
                      
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                      {product.location && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        📍 {product.location}
                      </Typography>                      
                      )}
                      <Chip 
                        label={product.type?.charAt(0).toUpperCase() + product.type?.slice(1)} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                      </Box>
                      {canRemove(product) && (
                        <Tooltip title="Remove Set">
                          <IconButton
                            size="small"
                            onClick={() => onRemoveProduct(product.id)}
                            sx={{ 
                              color: 'error.main',
                              '&:hover': { backgroundColor: 'error.light' }
                            }}
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  {/* Stats */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="primary.main" sx={{ fontSize: '1rem', fontWeight: 400 }}>
                        {product?.frameCount == 1 ? <CheckIcon/> : <CloseIcon/>}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Frames
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="success.main" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
                        {product?.panelCount || "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Panel
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="warning.main" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
                        {product?.panelWindowCount || "_"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Panel Window 
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="warning.main" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
                        {product?.netCount || "_"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Net
                      </Typography>
                    </Box>
                  </Box>

              
                </CardContent>
              </Card>
            </Box>
          );
        })
        )}
      </Box>
    </Box>
  );

  // Main Component with View Toggle
  return (
    <Box sx={{ height: 'auto', display: 'flex', flexDirection: 'column', overflow: 'visible' }}>
      {/* Compact Header */}
      <Paper sx={{ 
        p: 1, 
        mb: 1,
        bgcolor: 'grey.50',
        borderRadius: 1,
        flexShrink: 0
      }}>
        {/* Top Row - Title and Controls */}
        {/* <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Products
            </Typography>
            <Badge badgeContent={productCounts.filtered} color="primary">
              <Chip 
                label="Filtered" 
                size="small" 
                color="primary" 
                variant="outlined"
                sx={{ display: productCounts.filtered !== productCounts.total ? 'flex' : 'none' }}
              />
            </Badge>
          </Box>
          
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="cards">
              <Squares2X2Icon className="h-4 w-4" />
            </ToggleButton>
            <ToggleButton value="table">
              <ViewColumnsIcon className="h-4 w-4" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box> */}


        {/* Compact Tabs */}
        <Box>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 'auto',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 'auto',
                px: 1.5,
                py: 0.5,
                fontSize: '0.875rem',
                minHeight: '32px'
              },
              '& .MuiTabs-indicator': {
                height: 2
              }
            }}
          >
            <Tab label={`All (${products.length})`} />
            {typeCounts.door > 0 && ( <Tab label={`Doors (${typeCounts.door || 0})`} /> )}
            {typeCounts.window > 0 && ( <Tab label={`Windows (${typeCounts.window || 0})`} /> )}
            {typeCounts.net > 0 && ( <Tab label={`Nets (${typeCounts.net || 0})`} /> )}
            {typeCounts.ventilator > 0 && ( <Tab label={`Ventilators (${typeCounts.ventilator || 0})`} /> )}

          </Tabs>
        </Box>
      </Paper>

      {/* Content Area */}
      <Box sx={{ flex: 1 }}>
        {viewMode === 'cards' ? (
          <CardView />
        ) : (
          <TableView 
            filteredProducts={filteredProducts}
            theme={theme}
            isTablet={isTablet}
            canEdit={canEdit}
            onEditProduct={onEditProduct}
            canRemove={canRemove}
            onRemoveProduct={onRemoveProduct}
            onAddProduct={onAddProduct}
          />
        )}
      </Box>
    </Box>
  );
};

// Table View Component
const TableView: React.FC<{
  filteredProducts: Product[];
  theme: any;
  isTablet: boolean;
  canEdit: boolean;
  onEditProduct: (product: Product) => void;
  canRemove: (product: Product) => boolean;
  onRemoveProduct: (productId: string) => void;
  onAddProduct?: (productType?: string) => void;
}> = ({ filteredProducts, theme, isTablet, canEdit, onEditProduct, canRemove, onRemoveProduct, onAddProduct }) => (
  <TableContainer 
    component={Paper} 
    variant="outlined"
    sx={{ 
      height: 'auto',
      overflow: 'visible'
    }}
  >
    <Table stickyHeader size={isTablet ? 'small' : 'medium'}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 600, backgroundColor: theme.palette.grey[50] }}>
            Set Name
          </TableCell>
          <TableCell sx={{ fontWeight: 600, backgroundColor: theme.palette.grey[50] }}>
            Type
          </TableCell>
          <TableCell sx={{ fontWeight: 600, backgroundColor: theme.palette.grey[50] }}>
            Items
          </TableCell>
          <TableCell sx={{ fontWeight: 600, backgroundColor: theme.palette.grey[50] }}>
            Completed
          </TableCell>
          <TableCell sx={{ fontWeight: 600, backgroundColor: theme.palette.grey[50] }}>
            Label
          </TableCell>
          <TableCell sx={{ fontWeight: 600, backgroundColor: theme.palette.grey[50] }}>
            Location
          </TableCell>
          <TableCell sx={{ fontWeight: 600, backgroundColor: theme.palette.grey[50] }}>
            Subtype
          </TableCell>
          <TableCell 
            sx={{ 
              fontWeight: 600, 
              backgroundColor: theme.palette.grey[50],
              textAlign: 'center',
              width: '120px'
            }}
          >
            Actions
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {/* Add Product Row */}
        {onAddProduct && (
          <TableRow 
            sx={{ 
              '&:hover': { 
                backgroundColor: theme.palette.primary.light + '20'
              },
              cursor: 'pointer',
              border: '2px dashed',
              borderColor: 'primary.main',
              backgroundColor: 'primary.50'
            }}
            onClick={() => onAddProduct()}
          >
            <TableCell colSpan={8} sx={{ textAlign: 'center', py: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <PlusIcon className="h-5 w-5" style={{ color: theme.palette.primary.main }} />
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  Add New Set
                </Typography>
              </Box>
            </TableCell>
          </TableRow>
        )}
        
        {filteredProducts.map((product) => (
          <TableRow 
            key={product.id}
            sx={{ 
              '&:hover': { 
                backgroundColor: theme.palette.action.hover 
              },
              '&:last-child td': { 
                borderBottom: 0 
              }
            }}
          >
            <TableCell>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '200px'
                }}
              >
                {product.label || product.name || `Set ${filteredProducts.indexOf(product) + 1}`}
              </Typography>
            </TableCell>
            <TableCell>
              <Chip 
                label={product.type?.charAt(0).toUpperCase() + product.type?.slice(1)} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {product.total_items || 0}
              </Typography>
            </TableCell>
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  {product.completed_items || 0}
                </Typography>
                {product.total_items && product.total_items > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    ({Math.round(((product.completed_items || 0) / product.total_items) * 100)}%)
                  </Typography>
                )}
              </Box>
            </TableCell>
            <TableCell>
              {product.label ? (
                <Chip 
                  label={product.label} 
                  size="small" 
                  color="default" 
                  variant="outlined"
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              )}
            </TableCell>
            <TableCell>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '150px'
                }}
              >
                {product.location || '-'}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '150px',
                  fontSize: '0.8rem'
                }}
              >
                {formatProductSubtypes(product)}
              </Typography>
            </TableCell>
            <TableCell>
              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                {canEdit && (
                  <Tooltip title="Edit Set">
                    <IconButton
                      size="small"
                      onClick={() => {
                        onEditProduct(product);
                      }}
                      sx={{ 
                        color: 'primary.main',
                        '&:hover': { backgroundColor: 'primary.light' }
                      }}
                    >
                      <PencilIcon className="h-4 w-4" />
                    </IconButton>
                  </Tooltip>
                )}
                {canRemove(product) && (
                  <Tooltip title="Remove Set">
                    <IconButton
                      size="small"
                      onClick={() => onRemoveProduct(product.id)}
                      sx={{ 
                        color: 'error.main',
                        '&:hover': { backgroundColor: 'error.light' }
                      }}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

export default ProductTable;
